'use strict'

// Bare shared-library import + deferred-call wrapper — the shell's function
// modules all reach the shared barrel this way (circular-init safety).
import { shellSay } from './shellSharedDelegates.js'

// FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — the Home load-state contract for the
// four state keys the agent-roster fix (FALSE-ZERO-1) did NOT cover:
// `people`, `_homeWorkspaceActivity`, the ticket lists, and `reminders`.
//
// THE LAW (three states, one surface):
//   NOT LOADED          → a skeleton / '–'. Never a number.
//   LOADED, GENUINELY 0 → the honest zero plus its empty-state copy.
//   FAILED              → an error affordance ('!'). Never a zero.
// A surface that cannot tell state 1 from state 2 is the bug.
//
// ⚠️ WHY `Array.isArray(root.X)` IS NOT A LOADED SIGNAL HERE, MEASURED.
// The agent roster could use it because `shared/state.js` seeds `_agents:
// null`. This app's own schema seeds `people: []`, `reminders: []`,
// `tickets: []` and `myTickets: []` — so `Array.isArray` is TRUE from the
// first frame, before any producer has run. Reproduced 2026-08-20 against
// the real schema default (`state-app.js`): with `people: []` (seed) and a
// LOADED 2-agent roster, the parent's `teamHeadcountText` returned "2" and
// its share bar returned people 0 / agents 1 — a confident total and a
// confident 100%-agents split, both computed from a roster nobody had read.
// An `[]`-seeded key therefore needs a POSITIVE marker written by its
// producer; that is what `peopleLoaded` / `remindersLoaded` are, and why
// they are declared in `state-app.js` (an undeclared key is dropped by
// smbls 3.14 `root.update()`). Home's own ticket cache `_userTickets` is
// instead seeded `null` — the same "not read yet" convention as `_agents`
// and `_homeWorkspaceActivity` — because its one-shot request gate
// (`_homeTicketsLoaded`) is set BEFORE the fetch resolves and so can never
// double as the positive marker.
//
// ⚠️ AND WHY A CONSUMER FIX ALONE IS WORTHLESS. The parent's predecessor
// shipped a correct tri-state consumer that rendered a permanent '–' for
// four days, because no producer wrote the key on Home. Every status below
// names the producer that writes its marker, and each producer runs on
// Home. `root.tickets` has NO Home producer at all — see
// `homeTicketsStatus`.

// One-line status resolver shared by every key below: a positive marker,
// an error key, nothing else. Kept private so a consumer cannot invent a
// fifth state.
const _status = (loaded, error) => {
  if (error) return 'error'
  return loaded ? 'loaded' : 'loading'
}

// The glyphs. Same pair the agent-roster contract already renders, so the
// four families read identically on one screen.
export const HOME_UNKNOWN_GLYPH = '–'
export const HOME_ERROR_GLYPH = '!'

// Render a count through the contract. `count` is only evaluated in the
// loaded branch, so a caller can pass a thunk that assumes loaded data.
const _countText = (status, count) => {
  if (status === 'error') return HOME_ERROR_GLYPH
  if (status !== 'loaded') return HOME_UNKNOWN_GLYPH
  return String(count())
}

// ── FAMILY B — root.people ───────────────────────────────────────────
// Producers (all write `peopleLoaded`): the shell-mount prime peek
// (`app.js`), the boot loader and the workspace-switch reload
// (`functions/loadPrivateData.js`), and Home's context-menu Refresh
// (`pages/main.js` _refreshHomeData). The boot loader and the Refresh
// path also record `peopleError` on the fetch failure they previously
// only console.warn'd.
export const peopleStatus = (root) =>
  _status(!!root?.peopleLoaded && Array.isArray(root?.people), root?.peopleError)

export const peopleCountText = (root) => _countText(peopleStatus(root), () => root.people.length)

// A bundle sentence for a load-state title or subtitle: through the calling
// element's polyglot (`el.call('peopleTitle', root)` binds it, so the copy
// follows the session language), or the SHIPPED ENGLISH on a bare call — never
// a raw key. WS-SHELL-ENGLISH-LITERALS-OUTSIDE-THE-GATE-LIST-1: these four
// helpers used to return the English words themselves, so a Georgian board
// read "Couldn’t load reminders".

export function peopleTitle(root) {
  return peopleStatus(root) === 'error' ? shellSay(this, 'workspace.agents.membersLoadFailed') : ''
}

// ── FAMILY C — root._homeWorkspaceActivity ───────────────────────────
// Producer: `_loadHomeWorkspaceActivity` (`pages/main.js`), which runs on
// every Home load and on Refresh.
//
// BOTH LAYERS used to convert unknown into zero — the producer wrote
// `Number(res.usersNow ?? res.users_now) || 0`, so an ABSENT field became a
// well-formed 0 at the data layer, and its `.catch` recorded nothing; then
// every consumer applied `|| 0` again. The producer now writes `null` for a
// field the server did not send and records
// `_homeWorkspaceActivityError`, so a failed or partial read is
// distinguishable from a quiet workspace.
export const homeActivityStatus = (root) => {
  const a = root?._homeWorkspaceActivity
  return _status(!!a && typeof a === 'object', root?._homeWorkspaceActivityError)
}

// A single metric can be missing from an otherwise-loaded payload, so the
// per-metric read carries its own presence check on top of the payload
// status.
const _metric = (root, key) => {
  const a = root?._homeWorkspaceActivity
  const v = a && a[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

// The producer-side normaliser: a server field that is absent, empty or
// non-numeric is `null` — NOT 0. This is the line that stops "unknown"
// from becoming a well-formed zero at the data layer.
export const homeActivityMetric = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// The numeric "active now" read for callers that compose copy around it
// (the Watcher agent caption). `null` until positively read.
export const homeActiveNow = (root) =>
  homeActivityStatus(root) === 'loaded' ? _metric(root, 'usersNow') : null

export const homeActivityMetricText = (root, key) => {
  const status = homeActivityStatus(root)
  if (status === 'error') return HOME_ERROR_GLYPH
  const v = status === 'loaded' ? _metric(root, key) : null
  return v == null ? HOME_UNKNOWN_GLYPH : String(v)
}

export const homeActiveNowText = (root) => homeActivityMetricText(root, 'usersNow')
export const homeActiveTodayText = (root) => homeActivityMetricText(root, 'usersToday')

// Is the now-vs-today proportion unknown? A PROPORTION is the worst thing
// to render from an unread input: an absent count reads as an empty state,
// but a wrong fraction reads as a confident measurement. Drives which bar
// the analytics widget mounts — the real segmented one, or the skeleton.
export const homeActivityUnknown = (root) =>
  homeActivityStatus(root) !== 'loaded' ||
  _metric(root, 'usersNow') == null ||
  _metric(root, 'usersToday') == null

// Segment share for the analytics bar. Same shape as the team split bar:
// unknown collapses both segments to 0 (the skeleton takes over), a
// genuinely-zero day keeps the neutral empty track, and a nonzero share
// gets the 4% visual floor.
export const homeActivitySegFlex = (root, part) => {
  if (homeActivityUnknown(root)) return '0'
  const now = _metric(root, 'usersNow')
  const today = Math.max(_metric(root, 'usersToday'), now)
  if (!today) return part === 'now' ? '0.04' : '0.96'
  const frac = (part === 'now' ? now : today - now) / today
  return String(frac > 0 ? Math.max(frac, 0.04) : 0)
}

export function homeActivityTitle(root) {
  return homeActivityStatus(root) === 'error' ? shellSay(this, 'workspace.home.loadState.activity') : ''
}

// ── FAMILY D — the ticket lists ──────────────────────────────────────
// ⛔ `root.tickets` IS THE BOARD CACHE AND HAS NO HOME PRODUCER. It is
// populated only by a /tickets board visit, so `Array.isArray(root.tickets)
// ? root.tickets.length : 0` on Home was a permanent 0 on every cold load —
// the agent status caption read "Standing by" while the user had open work.
// Adding tri-state handling to that read would have rendered a skeleton
// forever, which is a different false answer, not a fix. Home's own
// producer is `_ensureHomeTickets` (`pages/main.js`) and its key is
// `_userTickets` (seeded `null`, written as an array — possibly `[]` — only
// by a resolved fetch); that is what this reads, together with the new
// `_homeTicketsError` the producer records on a failed fetch.
export const homeTicketsStatus = (root) =>
  _status(Array.isArray(root?._userTickets), root?._homeTicketsError)

// Assigned-to-me subset, seeded by the same producer in the same write.
export const homeMyTicketsStatus = (root) =>
  _status(
    Array.isArray(root?._userTickets) && Array.isArray(root?.myTickets),
    root?._homeTicketsError
  )

export const homeTicketsInFlight = (root) =>
  homeTicketsStatus(root) === 'loaded' ? root._userTickets.length : null

// FALSE-EMPTY-HOME-RECEIVED-GIVEN-LOADING-1 — the two ticket widgets that
// render a LIST (Home's Received / Given). They filter the widget ticket set
// themselves, so the contract takes the ALREADY-FILTERED count and only
// decides which body may be claimed: the skeleton while the set is unread,
// the error affordance on a failed read, and the widget's own empty copy
// ONLY once `_userTickets` is positively an array. A count derived from the
// board-cache fallback (`root.tickets`, no Home producer) can never promote
// a loading widget to a list — the status alone decides that.
export const homeTicketListState = (root, count) => {
  const status = homeTicketsStatus(root)
  if (status !== 'loaded') return status
  return count ? 'list' : 'empty'
}

// The matching numeral: '–' unread, '!' failed, the honest number once read.
export const homeTicketCountText = (root, count) => _countText(homeTicketsStatus(root), () => count)

export const homeMyTicketsCount = (root) =>
  homeMyTicketsStatus(root) === 'loaded' ? root.myTickets.length : null

// ── FAMILY E — root.reminders ────────────────────────────────────────
// Producers: Home's own inline records-plane fetch on the Reminders widget
// (`pages/main.js`) and the /reminders page's fetch (`pages/reminders.js`).
// Both now write `remindersLoaded` / `remindersError`. `_remindersLoadError`
// stays what it was — the records-plane CAPABILITY gate (403), which drives
// distinct "Reminders isn't enabled" copy rather than a load failure.
export const remindersStatus = (root) => {
  if (root?._remindersLoadError) return 'error'
  return _status(!!root?.remindersLoaded && Array.isArray(root?.reminders), root?.remindersError)
}

// "N due today" / "Personal follow-ups" — the widget subtitle and its
// numeral. An unloaded reminder set must not render "nothing due today".
export const remindersTodayText = (root, counts) =>
  _countText(remindersStatus(root), () => (counts || {}).today || 0)

export function remindersSubtitle(root, counts) {
  const status = remindersStatus(root)
  if (status === 'error') return shellSay(this, 'workspace.home.loadState.reminders')
  // An unread set shows the widget's descriptor — the skeleton body carries
  // the busy state; a renderable "Loading…" string is illegal (DESIGN_LANGUAGE
  // §9, lock renderable-loading-string). The numeral stays '–' meanwhile.
  if (status !== 'loaded') return shellSay(this, 'workspace.home.loadState.remindersDescriptor')
  const n = (counts || {}).today || 0
  return n
    ? shellSay(this, 'workspace.home.loadState.remindersDueToday').replace('{count}', () => String(n))
    : shellSay(this, 'workspace.home.loadState.remindersDescriptor')
}

// Which body the Reminders widget mounts: the skeleton, the error/empty
// copy, or the list. "Nothing on your list" from an unread set is the same
// class of lie as the zero — only a LOADED set may claim emptiness.
export const remindersListState = (root, counts) => {
  const status = remindersStatus(root)
  if (status !== 'loaded') return status
  return (counts || {}).all ? 'list' : 'empty'
}

export function remindersEmptyText(root) {
  return remindersStatus(root) === 'error'
    ? shellSay(this, 'workspace.home.loadState.reminders')
    : shellSay(this, 'workspace.home.loadState.remindersEmpty')
}
